import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Hr, Row, Spacer } from '@amzn/stencil-react-components/layout'
import { Text } from '@amzn/stencil-react-components/text'
import { IconBinSmall as IconBin, IconPlusSmall as IconPlus } from '@amzn/stencil-react-icons'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { ChatItemDTO } from 'src/models/dto/items/ChatItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import { ItemEditorTextInput } from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { ChatHandler } from 'src/services/EntityServices/ItemUpdateHandlers/ChatHandler'

export const ChatItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<ChatItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={ChatHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            />
            <Spacer height='S400' />
            <Col width={'100%'}>
                <ItemEditorTextInput
                    itemId={itemDTO.id}
                    dataTestId={'chat-item-header'}
                    disabled={editDisabled}
                    inputId='chat-header'
                    labelText='Chat Header'
                    placeholder='Enter conversation header'
                    value={itemDTO.chatHeader?.headerStringI18N[locale] || ''}
                    setValue={(nextValue: string) =>
                        ChatHandler.updateChatHeader(itemDTO.id, locale, nextValue)
                    }
                    validationErrorMessage={(
                        validationErrorEntity.validationErrors.chatHeader ?? []
                    ).join(', ')}
                    showError={showValidationError}
                />
            </Col>

            {itemDTO.chatMessages.map((chatMessage, index) => {
                return (
                    <Col key={index} dataTestId={`chat-item-message-row-${index}`}>
                        <Spacer height='S400' />
                        <Hr size='wide' color='neutral20' />
                        <Spacer height='S400' />
                        <Row justifyContent={'space-between'}>
                            <Text fontSize={'T300'} color={'neutral90'} fontWeight={'bold'}>
                                {' '}
                                Chat Message {index + 1}
                            </Text>
                            <Button
                                aria-label={`delete-message-${index + 1}`}
                                aria-disabled={
                                    itemDTO.chatMessages.length === 1 ||
                                    !isDefaultLocale(locale) ||
                                    editDisabled
                                }
                                dataTestId={`chat-item-message-${index}-delete-button`}
                                icon={<IconBin aria-hidden={true} />}
                                isDestructive={true}
                                variant={ButtonVariant.Tertiary}
                                onClick={() => {
                                    ChatHandler.deleteMessage(itemDTO.id, index)
                                }}
                            />
                        </Row>
                        <Col width={'100%'}>
                            <ItemEditorTextInput
                                inputId={`chat-${index}-message-header`}
                                value={chatMessage.messageHeaderI18N[locale] || ''}
                                dataTestId={`chat-item-message-${index}-header`}
                                placeholder={'Enter chat sender'}
                                disabled={editDisabled}
                                itemId={itemDTO.id}
                                setValue={(nextValue: string) => {
                                    ChatHandler.updateMessageHeader(
                                        itemDTO.id,
                                        locale,
                                        index,
                                        nextValue
                                    )
                                }}
                                validationErrorMessage={(
                                    validationErrorEntity.validationErrors.chatMessages ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'From (Chat sender)'}
                            />
                        </Col>
                        <Spacer height='S400' />
                        <Col width={'100%'}>
                            <ItemEditorTextInput
                                inputId={`chat-${index}-message`}
                                value={chatMessage.messageI18N[locale] || ''}
                                placeholder={'Enter the chat message'}
                                dataTestId={`chat-item-message-${index}-message`}
                                disabled={editDisabled}
                                itemId={itemDTO.id}
                                setValue={(nextValue: string) => {
                                    ChatHandler.updateMessage(itemDTO.id, locale, index, nextValue)
                                }}
                                validationErrorMessage={(
                                    validationErrorEntity.validationErrors.chatMessages ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'Chat message'}
                            />
                        </Col>
                    </Col>
                )
            })}

            <Spacer height='S400' />
            <Row justifyContent={'flex-end'}>
                <Button
                    aria-label={'add-message'}
                    dataTestId='add-message'
                    icon={<IconPlus aria-hidden={true} />}
                    variant={ButtonVariant.Tertiary}
                    onClick={() => {
                        ChatHandler.addMessage(itemDTO.id)
                    }}
                    aria-disabled={editDisabled}
                >
                    Add new message
                </Button>
            </Row>
        </>
    )
}
